/**
 * Stripe v15 / API 2026-04-22.dahlia compatibility helpers
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * ---------------------------------------------------------------------------
 * Why this module exists
 * ---------------------------------------------------------------------------
 *
 * Under Stripe API 2026-03-25.dahlia and onward, several invoice and line-item
 * fields moved or were renamed:
 *
 *   • Invoice.payment_intent             →  invoice.payments.data[0].payment.payment_intent
 *   • InvoiceLineItem.price              →  line.pricing.price_details (productId/priceId)
 *                                            +  line.parent.type      (one_time / recurring)
 *                                            +  line.parent.subscription_item_details.plan.interval
 *   • Invoice.total_tax_amounts          →  invoice.total_taxes
 *
 * These helpers provide a single fallback chain that works on BOTH API
 * generations: the new path is consulted only if the legacy path is absent.
 * The same code therefore runs correctly whether the backend is pinned to
 * the legacy API or to API 2026, and existing test fixtures using the
 * legacy shape continue to pass.
 *
 * FCR S291 DD-3: Extracted from thanksAdapter.ts to a single source of truth
 * so all three adapters (thanksAdapter, lifetime-adapter, subscription-adapter)
 * share one copy. Future API revisions are then a single-file change.
 *
 * Generic typing: every helper accepts ``unknown``-shaped inputs internally
 * and uses optional-chaining to read both shapes. Callers pass whatever
 * line/invoice type they have — Order, OrderData["invoice"], a raw
 * StripeObject — and the helpers return the same result. No runtime
 * dependency on a specific TypeScript shape.
 */

// ---------------------------------------------------------------------------
// Internal shape types (loose-typed for cross-adapter reuse)
// ---------------------------------------------------------------------------

interface InvoiceShape {
  payment_intent?: string | null;
  payments?: {
    data?: Array<{ payment?: { payment_intent?: string | null } }>;
  };
  total_tax_amounts?: Array<{ amount?: number }>;
  total_taxes?: Array<{ amount?: number }>;
}

interface LineItemShape {
  price?: {
    id?: string;
    product?: string;
    type?: string;
    recurring?: { interval?: string } | null;
  } | null;
  pricing?: {
    type?: string;
    price_details?: {
      product?: string;
      // price may be a bare string ID or a fully expanded Stripe Price object
      // (when lines.data.pricing.price_details.price is included in the expand list)
      price?:
        | string
        | {
            id?: string;
            recurring?: { interval?: string } | null;
            type?: string;
          }
        | null;
    };
  };
  parent?: {
    type?: string;
    subscription_item_details?: {
      // Legacy API: plan.interval used for billing-interval detection.
      plan?: { interval?: string } | null;
      // API 2026 fields present on Stripe SDK 15 line item objects.
      // Expanding these avoids TypeScript's "weak type" rejection when the
      // actual Stripe object carries no `plan` but has these fields instead.
      invoice_item?: string | null;
      proration?: boolean;
      proration_details?: unknown;
      subscription?: string | null;
      subscription_item?: string | null;
    } | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Return the payment_intent ID for an invoice across both API generations.
 *
 * Legacy: invoice.payment_intent
 * API 2026: invoice.payments.data[0].payment.payment_intent
 *
 * @returns The payment_intent ID, or null if neither path resolves it.
 */
export function getInvoicePaymentIntent(
  invoice: InvoiceShape | null | undefined,
): string | null {
  if (!invoice) return null;
  const legacy = invoice.payment_intent;
  if (legacy) return legacy;
  const modern = invoice.payments?.data?.[0]?.payment?.payment_intent;
  return modern ?? null;
}

/**
 * Return the total tax amount in cents for an invoice across both API generations.
 *
 * Legacy: invoice.total_tax_amounts[0].amount
 * API 2026: invoice.total_taxes[0].amount  (renamed)
 *
 * @returns Tax amount in cents (0 if absent).
 */
export function getInvoiceTaxAmount(
  invoice: InvoiceShape | null | undefined,
): number {
  if (!invoice) return 0;
  const legacy = invoice.total_tax_amounts?.[0]?.amount;
  if (typeof legacy === "number") return legacy;
  const modern = invoice.total_taxes?.[0]?.amount;
  return typeof modern === "number" ? modern : 0;
}

/**
 * Return the Stripe product ID for an invoice line item across both API generations.
 *
 * Legacy: line.price.product
 * API 2026: line.pricing.price_details.product
 *
 * @returns The product ID, or undefined if neither path resolves it.
 */
export function getLineProductId(
  item: LineItemShape | null | undefined,
): string | undefined {
  if (!item) return undefined;
  return (
    item.price?.product ??
    item.pricing?.price_details?.product ??
    undefined
  );
}

/**
 * Return the Stripe price ID for an invoice line item across both API generations.
 *
 * Legacy (pre-2025-03-31.basil): line.price.id
 * API 2026 (2025-03-31.basil+): line.pricing.price_details.price
 *
 * @returns The price ID string, or undefined if neither path resolves it.
 */
export function getLinePriceId(
  item: LineItemShape | null | undefined,
): string | undefined {
  if (!item) return undefined;
  // API 2026: pricing.price_details.price may be an expanded Price object or a bare string ID
  if (item.pricing?.type === "price_details") {
    const p = item.pricing.price_details?.price;
    if (typeof p === "string") return p;
    if (typeof p === "object" && p && "id" in p) return (p as { id?: string }).id ?? undefined;
  }
  // Legacy fallback
  return item.price?.id ?? undefined;
}

/**
 * Return the line item billing type ("recurring" | "one_time") across both API generations.
 *
 * Legacy: line.price.type
 * API 2026: line.parent.type ("invoice_item_details" → one_time, "subscription_item_details" → recurring)
 *
 * @returns "recurring" | "one_time" | undefined.
 */
export function getLineType(
  item: LineItemShape | null | undefined,
): "recurring" | "one_time" | undefined {
  if (!item) return undefined;
  const fromPrice = item.price?.type;
  if (fromPrice === "recurring" || fromPrice === "one_time") {
    return fromPrice;
  }
  if (item.parent?.type === "subscription_item_details") return "recurring";
  if (item.parent?.type === "invoice_item_details") return "one_time";
  return undefined;
}

/**
 * Return the recurring interval ("month" | "year" | etc.) across both API generations.
 *
 * Legacy (pre-2025-03-31.basil): line.price.recurring.interval
 * API 2026 (2025-03-31.basil+): line.pricing.price_details.price.recurring.interval
 *   (requires expand=['lines.data.pricing.price_details.price'] on the invoice fetch)
 *
 * Both line.price and line.parent.subscription_item_details.plan were removed
 * in Stripe API 2025-03-31.basil. Only the pricing.price_details path is valid
 * in API 2026-04-22.dahlia and later.
 *
 * @returns The interval string, or undefined.
 */
export function getLineInterval(
  item: LineItemShape | null | undefined,
): string | undefined {
  if (!item) return undefined;
  // API 2026: read from the expanded Price object inside pricing.price_details
  if (item.pricing?.type === "price_details") {
    const p = item.pricing.price_details?.price;
    if (typeof p === "object" && p && "recurring" in p) {
      const interval = (p as { recurring?: { interval?: string } }).recurring?.interval;
      if (interval) return interval;
    }
  }
  // Legacy fallback (pre-2025-03-31.basil)
  const fromPrice = item.price?.recurring?.interval;
  if (fromPrice) return fromPrice;
  return item.parent?.subscription_item_details?.plan?.interval;
}
